import { useState, useRef, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Design tokens ─────────────────────────────────────────────────────────── */
const C = {
  blue:    "#1E6FD9",
  teal:    "#14B8A6",
  green:   "#22C55E",
  bgBlue:  "#F0F7FF",
  bgGreen: "#ECFDF5",
  dark:    "#1E293B",
  mid:     "#475569",
  light:   "#94A3B8",
  white:   "#FFFFFF",
  border:  "#E2E8F0",
};
const FF = "'Inter', system-ui, sans-serif";

/* ── Helpers ───────────────────────────────────────────────────────────────── */
const GreenCheck = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink:0, marginTop:2 }}>
    <circle cx="10" cy="10" r="10" fill={C.teal} opacity=".15"/>
    <path d="M6 10.5l3 3 5-5" stroke={C.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BulletRow = ({ text, bold }) => (
  <div style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10 }}>
    <GreenCheck />
    <span style={{ fontFamily:FF, fontSize:14, color:C.dark, lineHeight:1.5 }}>
      {bold ? <strong>{bold}</strong> : null}{text}
    </span>
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom:`1px solid ${C.border}` }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ width:"100%", textAlign:"left", padding:"18px 0", display:"flex",
          justifyContent:"space-between", alignItems:"center", gap:12,
          background:"none", border:"none", cursor:"pointer", fontFamily:FF,
          fontWeight:600, fontSize:15, color:C.dark }}
        aria-expanded={open}
      >
        <span>{q}</span>
        <span style={{ color:C.blue, fontSize:20, flexShrink:0 }}>{open ? "−" : "+"}</span>
      </button>
      <div style={{ maxHeight:open?800:0, overflow:"hidden", transition:"max-height .3s ease",
        fontFamily:FF, fontSize:14, lineHeight:1.7, color:C.mid, paddingBottom:open?18:0 }}>
        {a}
      </div>
    </div>
  );
};

/* ── Main Page ─────────────────────────────────────────────────────────────── */
export default function WorkplaceWellness() {
  const formRef     = useRef(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [form, setForm]       = useState({ full_name:"", job_title:"", company:"",
    work_email:"", phone:"", employee_count:"", plan_interest:"", message:"", website:"" });
  const [errors, setErrors]   = useState({});
  const [submitting, setSub]  = useState(false);
  const [submitted, setSub2]  = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);

  /* ── Per-page SEO: index/follow, title, canonical, OG, Twitter, JSON-LD ── */
  useEffect(() => {
    const SITE = "https://neighborhoodboxingclub.us";
    const PAGE_TITLE = "Corporate Wellness Program Columbus OH | Neighborhood Boxing Club";
    const PAGE_DESC  = "Employee wellness program in Columbus, Ohio. Group boxing fitness for teams at $19.99 per employee/month. Boost health, morale and retention. Get a quote.";
    const origTitle  = document.title;
    document.title   = PAGE_TITLE;

    // Inject a head element and return it for cleanup
    const inj = (tag, attrs, id) => {
      const el = document.createElement(tag);
      el.id = id;
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "textContent") el.textContent = v;
        else el.setAttribute(k, v);
      });
      document.head.appendChild(el);
    };

    inj("meta",   { name:"robots",          content:"index, follow" },                    "ww-robots");
    inj("meta",   { name:"description",      content:PAGE_DESC },                         "ww-desc");
    inj("link",   { rel:"canonical",         href:`${SITE}/workplace-wellness` },          "ww-canonical");
    inj("meta",   { property:"og:title",     content:PAGE_TITLE },                        "ww-og-title");
    inj("meta",   { property:"og:description",content:PAGE_DESC },                        "ww-og-desc");
    inj("meta",   { property:"og:url",       content:`${SITE}/workplace-wellness` },       "ww-og-url");
    inj("meta",   { property:"og:type",      content:"website" },                         "ww-og-type");
    inj("meta",   { property:"og:image",     content:`${SITE}/workplace-wellness/hero-poster.jpg` }, "ww-og-img");
    inj("meta",   { name:"twitter:card",     content:"summary_large_image" },             "ww-tw-card");
    inj("meta",   { name:"twitter:title",    content:PAGE_TITLE },                        "ww-tw-title");
    inj("meta",   { name:"twitter:description",content:PAGE_DESC },                      "ww-tw-desc");

    // FAQPage schema
    inj("script", { type:"application/ld+json", textContent: JSON.stringify({
      "@context":"https://schema.org", "@type":"FAQPage",
      "mainEntity":[
        { "@type":"Question", "name":"How much does the group plan cost?",
          "acceptedAnswer":{ "@type":"Answer", "text":"$19.99 per employee per month for teams of 10 to 299 employees, paid as one single monthly payment." }},
        { "@type":"Question", "name":"How long is the contract?",
          "acceptedAnswer":{ "@type":"Answer", "text":"The group plan has a 4-month initial commitment, then goes month-to-month." }},
        { "@type":"Question", "name":"How is it billed?",
          "acceptedAnswer":{ "@type":"Answer", "text":"Monthly Stripe invoice. ACH bank transfer preferred; card or company check also accepted." }},
        { "@type":"Question", "name":"What about companies with 300 or more employees?",
          "acceptedAnswer":{ "@type":"Answer", "text":"Volume pricing is available for larger organizations. Contact us to discuss your needs." }},
        { "@type":"Question", "name":"How do individual employees get the $49 per month rate?",
          "acceptedAnswer":{ "@type":"Answer", "text":"The $49 rate is discounted from the normal $79.99 per month rate. First, book a free first class online at neighborhoodboxingclub.us. Then bring a work ID and a photo ID to the gym to activate the $49 rate in person on our tablet. The discounted rate cannot be purchased online or by phone." }},
      ]
    })}, "ww-faq-ld");

    // LocalBusiness / HealthClub schema
    inj("script", { type:"application/ld+json", textContent: JSON.stringify({
      "@context":"https://schema.org", "@type":"HealthClub",
      "name":"Neighborhood Boxing Club",
      "url":SITE, "telephone":"+16145476096",
      "address":{ "@type":"PostalAddress", "streetAddress":"4324 N High St",
        "addressLocality":"Columbus","addressRegion":"OH","postalCode":"43214","addressCountry":"US" },
    })}, "ww-biz-ld");

    // VideoObject schema
    inj("script", { type:"application/ld+json", textContent: JSON.stringify({
      "@context":"https://schema.org", "@type":"VideoObject",
      "name":"Corporate Wellness Program for Columbus Employers | Neighborhood Boxing Club",
      "description":PAGE_DESC,
      "thumbnailUrl":"https://img.youtube.com/vi/YxgyUpTqfEI/maxresdefault.jpg",
      "embedUrl":"https://www.youtube.com/embed/YxgyUpTqfEI",
      "contentUrl":"https://youtu.be/YxgyUpTqfEI",
      "uploadDate":"2026-10-29",
    })}, "ww-vid-ld");

    // Inter font
    inj("link", { rel:"stylesheet", id:"ww-font-inner",
      href:"https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap",
    }, "ww-font");

    return () => {
      document.title = origTitle;
      ["ww-robots","ww-desc","ww-canonical","ww-og-title","ww-og-desc","ww-og-url","ww-og-type",
       "ww-og-img","ww-tw-card","ww-tw-title","ww-tw-desc","ww-faq-ld","ww-biz-ld","ww-vid-ld","ww-font",
      ].forEach(id => document.getElementById(id)?.remove());
    };
  }, []);

  /* Sync selectedPlan into form */
  useEffect(() => {
    if (selectedPlan) setForm(f => ({ ...f, plan_interest: selectedPlan }));
  }, [selectedPlan]);

  const scrollToForm = (plan = "") => {
    if (plan) setSelectedPlan(plan);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 50);
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim())      e.full_name      = "Required";
    if (!form.job_title.trim())      e.job_title      = "Required";
    if (!form.company.trim())        e.company        = "Required";
    if (!form.work_email.trim())     e.work_email     = "Required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.work_email)) e.work_email = "Enter a valid email";
    if (!form.phone.trim())          e.phone          = "Required";
    if (!form.employee_count)        e.employee_count = "Required";
    else if (isNaN(Number(form.employee_count)) || Number(form.employee_count) < 1) e.employee_count = "Enter a valid number";
    if (!form.plan_interest)         e.plan_interest  = "Required";
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.website) return; // honeypot
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSub(true);
    try {
      await axios.post(`${API}/corporate-wellness-leads`, {
        ...form, employee_count: Number(form.employee_count)
      });
      setSub2(true);
    } catch (err) {
      setErrors({ _submit: err.response?.data?.detail || "Submission failed. Please try again." });
    } finally { setSub(false); }
  };

  const field = (key, label, type="text", required=true) => (
    <div style={{ marginBottom:18 }}>
      <label style={{ display:"block", fontFamily:FF, fontWeight:600, fontSize:13,
        color:C.dark, marginBottom:6 }} htmlFor={`ww-${key}`}>
        {label}{required && <span style={{ color:"#EF4444" }}> *</span>}
      </label>
      {type === "textarea" ? (
        <textarea id={`ww-${key}`} rows={4} value={form[key]}
          onChange={e => setForm(f => ({...f, [key]:e.target.value}))}
          style={{ width:"100%", padding:"10px 14px", border:`1.5px solid ${errors[key]?"#EF4444":"#BAE6FD"}`,
            borderRadius:10, fontFamily:FF, fontSize:14, color:C.dark,
            outline:"none", boxSizing:"border-box", background:"#E0F2FE" }} />
      ) : (
        <input id={`ww-${key}`} type={type} value={form[key]}
          onChange={e => setForm(f => ({...f, [key]:e.target.value}))}
          style={{ width:"100%", padding:"10px 14px", border:`1.5px solid ${errors[key]?"#EF4444":"#BAE6FD"}`,
            borderRadius:10, fontFamily:FF, fontSize:14, color:C.dark,
            outline:"none", boxSizing:"border-box", background:"#E0F2FE" }} />
      )}
      {errors[key] && <p style={{ fontFamily:FF, fontSize:12, color:"#EF4444", margin:"4px 0 0" }}>{errors[key]}</p>}
    </div>
  );

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily:FF, background:C.white, color:C.dark, overflowX:"hidden" }}>

      {/* ── Minimal Header ────────────────────────────────────────────────── */}
      <header style={{ background:C.white, borderBottom:`1px solid ${C.border}`, position:"sticky",
        top:0, zIndex:50, boxSizing:"border-box" }}>
        <div style={{ maxWidth:1120, margin:"0 auto", padding:"0 20px", height:64,
          display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <a href="/" style={{ textDecoration:"none" }}>
            <img src="/nbc_logo.jpg" alt="Neighborhood Boxing Club" style={{ height:40, objectFit:"contain" }} />
          </a>
          <button onClick={() => scrollToForm()}
            style={{ background:C.blue, color:C.white, border:"none", borderRadius:8,
              padding:"10px 22px", fontFamily:FF, fontWeight:700, fontSize:14, cursor:"pointer" }}
            data-testid="ww-talk-btn">
            Talk to Us
          </button>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{ position:"relative", minHeight:"520px", display:"flex",
        alignItems:"center", overflow:"hidden" }}>
        <video
          autoPlay muted loop playsInline
          poster="/workplace-wellness/hero-poster.jpg"
          style={{ position:"absolute", inset:0, width:"100%", height:"100%",
            objectFit:"cover", zIndex:0 }}
          data-testid="ww-hero-video"
        >
          <source src="/videos/workplace-wellness-hero.mp4" type="video/mp4" />
        </video>
        <div style={{ position:"absolute", inset:0, zIndex:1,
          background:"linear-gradient(135deg, rgba(30,111,217,.82) 0%, rgba(20,184,166,.72) 100%)" }} />
        <div style={{ position:"relative", zIndex:2, maxWidth:760, margin:"0 auto",
          padding:"60px 24px 48px", textAlign:"center" }}>
          <p style={{ fontFamily:FF, fontWeight:700, fontSize:13, letterSpacing:"2px",
            textTransform:"uppercase", color:"rgba(255,255,255,.85)", marginBottom:12 }}>
            NBC Corporate Wellness Partner Program
          </p>
          <h1 style={{ fontFamily:FF, fontWeight:900, fontSize:"clamp(26px,4.5vw,44px)",
            lineHeight:1.15, color:C.white, margin:"0 0 10px" }}>
            Corporate Wellness Program for Columbus Employers
          </h1>
          <p style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(22px,3.5vw,36px)",
            lineHeight:1.2, color:C.white, margin:"0 0 16px", opacity:.92 }}>
            Healthier, Less-Stressed Teams Start Here.
          </p>
          <p style={{ fontFamily:FF, fontSize:"clamp(14px,1.8vw,17px)", color:"rgba(255,255,255,.88)",
            lineHeight:1.65, margin:"0 0 24px", maxWidth:580, marginLeft:"auto", marginRight:"auto" }}>
            Coach-led group boxing fitness for every level, minutes from downtown Columbus.
          </p>

          {/* YouTube video embed — responsive 16:9, no horizontal scroll on phones */}
          <div style={{ width:"100%", maxWidth:640, margin:"0 auto 28px" }}>
            <div style={{ position:"relative", paddingBottom:"56.25%", borderRadius:16,
              overflow:"hidden", boxShadow:"0 8px 32px rgba(0,0,0,0.4)" }}>
              <iframe
                style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }}
                src="https://www.youtube-nocookie.com/embed/YxgyUpTqfEI?rel=0&modestbranding=1&playsinline=1"
                title="Neighborhood Boxing Club Corporate Wellness Program"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          <div style={{ display:"flex", gap:14, flexWrap:"wrap", justifyContent:"center" }}>
            <button onClick={() => scrollToForm()}
              style={{ background:C.white, color:C.blue, border:"none", borderRadius:10,
                padding:"14px 28px", fontFamily:FF, fontWeight:700, fontSize:15, cursor:"pointer" }}>
              Request Partner Info
            </button>
            <button onClick={() => document.getElementById("ww-plans")?.scrollIntoView({behavior:"smooth"})}
              style={{ background:"rgba(255,255,255,.15)", color:C.white, border:"2px solid rgba(255,255,255,.5)",
                borderRadius:10, padding:"14px 28px", fontFamily:FF, fontWeight:700, fontSize:15, cursor:"pointer" }}>
              See the Plans
            </button>
          </div>
        </div>
      </section>

      {/* ── Benefits ──────────────────────────────────────────────────────── */}
      <section style={{ background:C.bgBlue, padding:"72px 24px" }}>
        <div style={{ maxWidth:1120, margin:"0 auto" }}>
          <h2 style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(24px,4vw,36px)",
            textAlign:"center", color:C.dark, marginBottom:8 }}>
            Employee Wellness Benefits That Boost Morale and Retention
          </h2>
          <p style={{ textAlign:"center", color:C.mid, marginBottom:48, fontFamily:FF }}>
            A workplace wellness benefit employees actually use.
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:20 }}>
            {[
              { icon:"⚡", title:"Same-Day Stress Relief",
                body:"One session of moderate-to-vigorous exercise can reduce anxiety and improve sleep, per the U.S. Physical Activity Guidelines, 2nd ed." },
              { icon:"🔥", title:"Fight Burnout",
                body:"An hour that's theirs — phones away, coach in charge." },
              { icon:"⭐", title:"A Benefit People Feel",
                body:"A visible, in-person perk that supports retention." },
              { icon:"🤝", title:"Team Building That Sticks",
                body:"Co-workers train side by side." },
              { icon:"📋", title:"Turnkey for HR",
                body:"One agreement, one roster, one invoice." },
            ].map(b => (
              <div key={b.title} style={{ background:C.white, borderRadius:16, padding:"28px 22px",
                boxShadow:"0 2px 12px rgba(30,111,217,.07)" }}>
                <div style={{ fontSize:28, marginBottom:12 }}>{b.icon}</div>
                <h3 style={{ fontFamily:FF, fontWeight:700, fontSize:15, color:C.dark, marginBottom:8 }}>{b.title}</h3>
                <p style={{ fontFamily:FF, fontSize:13, color:C.mid, lineHeight:1.6, margin:0 }}>{b.body}</p>
              </div>
            ))}
          </div>
          <p style={{ textAlign:"center", marginTop:24, fontFamily:FF, fontSize:12, color:C.light }}>
            Source:{" "}
            <a href="https://www.cdc.gov/physical-activity-basics/health-benefits/adults.html"
              target="_blank" rel="noreferrer" style={{ color:C.blue }}>
              CDC Physical Activity Health Benefits for Adults
            </a>
          </p>
        </div>
      </section>

      {/* ── What Classes Look Like ────────────────────────────────────────── */}
      <section style={{ background:C.white, padding:"72px 24px" }}>
        <div style={{ maxWidth:1120, margin:"0 auto" }}>
          <h2 style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(22px,4vw,34px)",
            textAlign:"center", color:C.dark, marginBottom:8 }}>
            What classes look like
          </h2>
          <p style={{ textAlign:"center", color:C.mid, marginBottom:40, fontFamily:FF, fontSize:15 }}>
            Coach-led boxing fitness: pads, bags, footwork and conditioning.
            No sparring required, no experience needed.
          </p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:20 }}>
            {[
              { src:"/workplace-wellness/class-bags.jpg",  title:"Wrap up & warm up",
                alt:"Employee wrapping hands before group boxing class — workplace wellness benefit Columbus Ohio",
                body:"New? We'll show you how to wrap your hands. No experience needed." },
              { src:"/workplace-wellness/class-coach.jpg",  title:"Coach-led groups",
                alt:"Coach-led group boxing fitness class — employee wellness program Neighborhood Boxing Club Columbus",
                body:"A coach runs every class and adjusts for each level." },
              { src:"/workplace-wellness/class-wraps.jpg",  title:"Pads & bags",
                alt:"Employees taking a group boxing fitness class at Neighborhood Boxing Club in Columbus Ohio",
                body:"Full-body work that burns off stress. No sparring required." },
              { src:"/workplace-wellness/class-gym.jpg",    title:"Our gym space",
                alt:"Neighborhood Boxing Club gym — rows of heavy bags at 4324 N High St Clintonville Columbus Ohio",
                body:"Rows of heavy bags at 4324 N High St. Ask about a private team session." },
            ].map(c => (
              <div key={c.title} style={{ borderRadius:16, overflow:"hidden",
                boxShadow:"0 2px 12px rgba(0,0,0,.09)" }}>
                <div style={{ height:200, overflow:"hidden" }}>
                  <img src={c.src} alt={c.alt} loading="lazy"
                    style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                </div>
                <div style={{ padding:"18px 16px" }}>
                  <h3 style={{ fontFamily:FF, fontWeight:700, fontSize:15, color:C.dark, marginBottom:6 }}>{c.title}</h3>
                  <p style={{ fontFamily:FF, fontSize:13, color:C.mid, lineHeight:1.55, margin:0 }}>{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans ─────────────────────────────────────────────────────────── */}
      <section id="ww-plans" style={{ background:C.bgBlue, padding:"72px 24px" }}>
        <div style={{ maxWidth:1120, margin:"0 auto" }}>
          <h2 style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(24px,4vw,36px)",
            textAlign:"center", color:C.dark, marginBottom:8 }}>
            Group Fitness Plans for Teams of 10 to 299
          </h2>
          <p style={{ textAlign:"center", color:C.mid, marginBottom:48, fontFamily:FF }}>
            No tiers. No haggling. One flat group rate for teams of 10 to 299.
          </p>

          {/* Three-column pricing */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:20, alignItems:"start" }}>

            {/* Column 1: Individual */}
            <div style={{ background:C.white, borderRadius:16, padding:"28px 24px",
              boxShadow:"0 2px 16px rgba(0,0,0,.07)", display:"flex", flexDirection:"column" }}>
              <h3 style={{ fontFamily:FF, fontWeight:700, fontSize:16, color:C.dark,
                textAlign:"center", marginBottom:16 }}>Individual Employee Rate</h3>
              <div style={{ textAlign:"center", marginBottom:8 }}>
                <span style={{ fontFamily:FF, fontSize:16, color:C.light, textDecoration:"line-through",
                  marginRight:8 }}>$79.99</span>
                <span style={{ fontFamily:FF, fontWeight:900, fontSize:42, color:C.dark }}>$49</span>
              </div>
              <p style={{ fontFamily:FF, fontSize:13, color:C.mid, textAlign:"center", marginBottom:12 }}>
                per employee / month
              </p>
              <div style={{ background:C.bgGreen, border:`1px solid ${C.teal}`,
                borderRadius:8, padding:"6px 12px", textAlign:"center", marginBottom:20 }}>
                <span style={{ fontFamily:FF, fontWeight:700, fontSize:13, color:C.teal }}>
                  Save $30.99/month off the normal rate
                </span>
              </div>
              <div style={{ flex:1 }}>
                <BulletRow text="Company signs a simple partner agreement, no cost to the company" />
                <BulletRow text="Free first class: book online" />
                <BulletRow bold="$49 rate purchased in person only" text=", at the gym with work ID + photo ID" />
                <BulletRow text="Not sold online or by phone" />
                <BulletRow text="Month-to-month, no long contract" />
                <BulletRow text="All group classes, all levels" />
              </div>
              <button onClick={() => scrollToForm("Individual Employee Plan ($49/mo)")}
                style={{ marginTop:24, width:"100%", padding:"13px 0", borderRadius:24,
                  border:`2px solid ${C.blue}`, background:C.white, color:C.blue,
                  fontFamily:FF, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                Request Info
              </button>
            </div>

            {/* Column 2: Group — highlighted */}
            <div style={{ background:C.white, borderRadius:16, padding:"28px 24px",
              border:`2.5px solid ${C.teal}`, boxShadow:`0 8px 32px rgba(20,184,166,.18)`,
              display:"flex", flexDirection:"column", position:"relative" }}>
              <div style={{ position:"absolute", top:-16, left:"50%", transform:"translateX(-50%)",
                background:C.teal, color:C.white, borderRadius:999, padding:"6px 20px",
                fontFamily:FF, fontWeight:800, fontSize:12, letterSpacing:"1px",
                whiteSpace:"nowrap", textTransform:"uppercase" }}>
                ★ ONE FLAT RATE · BEST VALUE
              </div>
              <h3 style={{ fontFamily:FF, fontWeight:700, fontSize:16, color:C.dark,
                textAlign:"center", marginTop:8, marginBottom:16 }}>Company Group Plan</h3>
              <div style={{ textAlign:"center", marginBottom:4 }}>
                <span style={{ fontFamily:FF, fontWeight:900, fontSize:48, color:C.teal }}>$19.99</span>
              </div>
              <p style={{ fontFamily:FF, fontSize:13, color:C.mid, textAlign:"center", marginBottom:16 }}>
                per employee / month
              </p>
              <div style={{ background:C.bgGreen, border:`1px solid ${C.teal}`, borderRadius:10,
                padding:"12px 16px", marginBottom:20, textAlign:"center" }}>
                <p style={{ fontFamily:FF, fontWeight:800, fontSize:14, color:C.teal, margin:0, lineHeight:1.5 }}>
                  Teams of 10–299 employees<br/>
                  One flat rate · One single monthly payment
                </p>
              </div>
              <div style={{ flex:1 }}>
                <BulletRow text="Every rostered employee covered" />
                <BulletRow text="Same $19.99 for every team from 10 to 299" />
                <BulletRow text="No tiers, no negotiation needed" />
                <BulletRow text="4-month initial commitment, then month-to-month" />
                <BulletRow text="Stripe invoice (ACH preferred, card accepted) or company check" />
              </div>
              <button onClick={() => scrollToForm("Company Group Plan ($19.99/employee/mo)")}
                style={{ marginTop:24, width:"100%", padding:"13px 0", borderRadius:24,
                  border:"none", background:C.teal, color:C.white,
                  fontFamily:FF, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                Request Group Plan Info
              </button>
            </div>

            {/* Column 3: Larger Orgs */}
            <div style={{ background:C.white, borderRadius:16, padding:"28px 24px",
              boxShadow:"0 2px 16px rgba(0,0,0,.07)", display:"flex", flexDirection:"column" }}>
              <h3 style={{ fontFamily:FF, fontWeight:700, fontSize:16, color:C.dark,
                textAlign:"center", marginBottom:16 }}>Larger Organizations</h3>
              <div style={{ textAlign:"center", marginBottom:6 }}>
                <span style={{ fontFamily:FF, fontWeight:900, fontSize:38, color:C.blue }}>
                  Let's talk.
                </span>
              </div>
              <p style={{ fontFamily:FF, fontSize:13, color:C.mid, textAlign:"center", marginBottom:20 }}>
                300+ employees
              </p>
              <div style={{ flex:1 }}>
                <BulletRow text="Volume pricing available" />
                <BulletRow text="Built around what your organization needs" />
                <BulletRow text="Private team sessions on request" />
              </div>
              <button onClick={() => scrollToForm("Volume pricing (300+ employees)")}
                style={{ marginTop:24, width:"100%", padding:"13px 0", borderRadius:24,
                  border:`2px solid ${C.blue}`, background:C.white, color:C.blue,
                  fontFamily:FF, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                Let's Talk
              </button>
            </div>
          </div>

          {/* Shared note under all three cards */}
          <p style={{ textAlign:"center", fontFamily:FF, fontSize:13, color:C.mid,
            marginTop:24, lineHeight:1.6 }}>
            Group plan: 4-month initial commitment, then month-to-month.
            Individual rate: month-to-month, sign up in person with work ID + photo ID.
          </p>

          {/* How employees get the $49 rate — 2-step callout */}
          <div style={{ marginTop:32, background:C.bgGreen,
            border:`1.5px solid ${C.teal}`, borderRadius:16, padding:"28px 28px 24px" }}>
            <h3 style={{ fontFamily:FF, fontWeight:800, fontSize:17, color:C.dark,
              textAlign:"center", marginBottom:6 }}>
              How employees get the{" "}
              <span style={{ color:C.teal }}>$49 rate</span>{" "}
              <span style={{ fontWeight:400, color:C.mid, fontSize:14 }}>(normally $79.99/mo)</span>
            </h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",
              gap:20, marginTop:20 }}>
              <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:C.teal,
                  color:C.white, display:"flex", alignItems:"center", justifyContent:"center",
                  fontFamily:FF, fontWeight:800, fontSize:14, flexShrink:0 }}>1</div>
                <div>
                  <p style={{ fontFamily:FF, fontWeight:700, fontSize:14, color:C.dark, margin:"0 0 4px" }}>
                    Book your FREE first class online.
                  </p>
                  <p style={{ fontFamily:FF, fontSize:13, color:C.mid, margin:0 }}>
                    Sign up and book at neighborhoodboxingclub.us.
                  </p>
                </div>
              </div>
              <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:C.teal,
                  color:C.white, display:"flex", alignItems:"center", justifyContent:"center",
                  fontFamily:FF, fontWeight:800, fontSize:14, flexShrink:0 }}>2</div>
                <div>
                  <p style={{ fontFamily:FF, fontWeight:700, fontSize:14, color:C.dark, margin:"0 0 4px" }}>
                    Bring your work ID + photo ID to the gym
                  </p>
                  <p style={{ fontFamily:FF, fontSize:13, color:C.mid, margin:0 }}>
                    to activate your $49 rate in person on our tablet.
                  </p>
                </div>
              </div>
            </div>
            <p style={{ fontFamily:FF, fontWeight:700, fontSize:13, color:C.teal,
              textAlign:"center", marginTop:20 }}>
              The $49 employee rate cannot be purchased online or by phone. In person only.
            </p>
            <div style={{ textAlign:"center", marginTop:16 }}>
              <a href="https://neighborhoodboxingclub.us/dashboard/classes"
                target="_blank" rel="noreferrer"
                style={{ display:"inline-block", background:C.green, color:C.white,
                  borderRadius:24, padding:"13px 28px", fontFamily:FF, fontWeight:700,
                  fontSize:14, textDecoration:"none" }}>
                Book Your Free First Class
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section style={{ background:C.white, padding:"72px 24px" }}>
        <div style={{ maxWidth:1120, margin:"0 auto" }}>
          <h2 style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(24px,4vw,36px)",
            textAlign:"center", color:C.dark, marginBottom:48 }}>
            How the Workplace Wellness Program Works
          </h2>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:24 }}>
            {[
              { n:"1", title:"Sign the agreement",
                body:"One official NBC Corporate Wellness agreement." },
              { n:"2", title:"Get employees set up",
                body:"Group: HR sends the roster. $49 rate: book a free class online, then activate in person with work ID + photo ID." },
              { n:"3", title:"Employees train",
                body:"Any group class that fits their schedule. All levels." },
              { n:"4", title:"One monthly payment",
                body:"Group plan: one Stripe invoice or check each month. Roster changes are easy." },
            ].map(s => (
              <div key={s.n} style={{ background:C.bgBlue, borderRadius:16, padding:"28px 22px" }}>
                <div style={{ width:40, height:40, borderRadius:"50%", background:C.blue,
                  color:C.white, display:"flex", alignItems:"center", justifyContent:"center",
                  fontFamily:FF, fontWeight:800, fontSize:18, marginBottom:14 }}>{s.n}</div>
                <h3 style={{ fontFamily:FF, fontWeight:700, fontSize:15, color:C.dark, marginBottom:8 }}>{s.title}</h3>
                <p style={{ fontFamily:FF, fontSize:13, color:C.mid, lineHeight:1.6, margin:0 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section style={{ background:C.bgBlue, padding:"72px 24px" }}>
        <div style={{ maxWidth:740, margin:"0 auto" }}>
          <h2 style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(24px,4vw,34px)",
            textAlign:"center", color:C.dark, marginBottom:40 }}>
            Frequently Asked Questions
          </h2>
          {[
            { q:"Do employees need boxing experience?",
              a:"No. Classes are coach-led and built for all levels. There is no sparring required." },
            { q:"Is there a minimum for the group plan?",
              a:"Yes. The group plan is for teams of 10 to 299 employees, at one flat rate of $19.99 per employee per month, paid as one monthly payment." },
            { q:"How does billing work?",
              a:"The group plan is one single monthly payment for the whole team under contract. It is billed by Stripe invoice (ACH bank transfer preferred, card accepted) or company check. With the individual rate the company is never billed: each employee pays NBC $49/month directly." },
            { q:"What is the contract term?",
              a:"The group plan has a 4-month initial commitment, then goes month-to-month. The individual rate is month-to-month." },
            { q:"How do employees get the $49 rate?",
              a:"The $49 rate is discounted from the normal $79.99/month, a savings of $30.99/month. Two steps: First, book a FREE first class online at https://neighborhoodboxingclub.us/dashboard/classes. Then bring a work ID and a photo ID to the gym and activate the $49 rate in person on the tablet. The $49 rate cannot be purchased online or by phone." },
            { q:"We have 300+ employees. What does it cost?",
              a:"Larger organizations get volume pricing tailored to their needs. The $19.99 group rate is flat and not negotiable for teams of 10 to 299. Fill out the form below or text Ron at (614) 547-6096." },
            { q:"Can we add or remove employees?",
              a:"Yes. Send roster updates to NBC." },
            { q:"Where is the gym?",
              a:"4324 N High St, Columbus, OH 43214." },
            { q:"Can we book a private team session?",
              a:"Ask us on the form below and we will set it up." },
          ].map((item, i) => (
            <FaqItem key={i} q={item.q} a={item.a} />
          ))}
        </div>
      </section>

      {/* ── Lead Form ─────────────────────────────────────────────────────── */}
      <section ref={formRef} style={{ background:C.white, padding:"72px 24px" }} id="ww-form">
        <div style={{ maxWidth:620, margin:"0 auto" }}>
          <h2 style={{ fontFamily:FF, fontWeight:800, fontSize:"clamp(22px,4vw,34px)",
            color:C.dark, textAlign:"center", marginBottom:8 }}>
            Let's Build Your Team's Wellness Benefit
          </h2>
          <p style={{ fontFamily:FF, color:C.mid, textAlign:"center", marginBottom:40 }}>
            Fill out the form and we'll be in touch within one business day.
          </p>

          {submitted ? (
            <div style={{ background:C.bgGreen, border:`1.5px solid ${C.teal}`, borderRadius:16,
              padding:"40px 32px", textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:16 }}>✅</div>
              <h3 style={{ fontFamily:FF, fontWeight:800, fontSize:20, color:C.dark, marginBottom:10 }}>
                Request received!
              </h3>
              <p style={{ fontFamily:FF, fontSize:15, color:C.mid, margin:0 }}>
                Thanks for reaching out. We'll be in touch within one business day.
                Questions? Text Ron at <strong>(614) 547-6096</strong>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <input name="website" value={form.website}
                onChange={e => setForm(f => ({...f, website:e.target.value}))}
                style={{ position:"absolute", left:"-9999px", opacity:0 }}
                tabIndex={-1} autoComplete="off" aria-hidden="true" />

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
                <div>{field("full_name","Full Name")}</div>
                <div>{field("job_title","Job Title")}</div>
              </div>
              {field("company","Company")}
              {field("work_email","Work Email","email")}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
                <div>{field("phone","Phone","tel")}</div>
                <div>{field("employee_count","Number of Employees","number")}</div>
              </div>
              <div style={{ marginBottom:18 }}>
                <label style={{ display:"block", fontFamily:FF, fontWeight:600, fontSize:13,
                  color:C.dark, marginBottom:6 }} htmlFor="ww-plan">
                  Plan Interest <span style={{ color:"#EF4444" }}>*</span>
                </label>
                <select id="ww-plan" value={form.plan_interest}
                  onChange={e => setForm(f => ({...f, plan_interest:e.target.value}))}
                  style={{ width:"100%", padding:"10px 14px", border:`1.5px solid ${errors.plan_interest?"#EF4444":"#BAE6FD"}`,
                    borderRadius:10, fontFamily:FF, fontSize:14, color:form.plan_interest?C.dark:"#64748B",
                    outline:"none", background:"#E0F2FE", boxSizing:"border-box" }}>
                  <option value="">Select a plan…</option>
                  <option value="Individual Employee Plan ($49/mo)">Individual Employee Plan ($49/mo)</option>
                  <option value="Company Group Plan ($19.99/employee/mo)">Company Group Plan ($19.99/employee/mo)</option>
                  <option value="Volume pricing (300+ employees)">Volume pricing (300+ employees)</option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
                {errors.plan_interest && <p style={{ fontFamily:FF, fontSize:12, color:"#EF4444", margin:"4px 0 0" }}>{errors.plan_interest}</p>}
              </div>
              {field("message","Message (optional)","textarea",false)}

              {errors._submit && (
                <p style={{ fontFamily:FF, fontSize:13, color:"#EF4444", textAlign:"center", marginBottom:12 }}>
                  {errors._submit}
                </p>
              )}
              <button type="submit" disabled={submitting}
                style={{ width:"100%", padding:"15px 0", borderRadius:12, border:"none",
                  background: submitting ? C.light : C.blue, color:C.white,
                  fontFamily:FF, fontWeight:700, fontSize:16, cursor: submitting?"not-allowed":"pointer" }}
                data-testid="ww-submit-btn">
                {submitting ? "Sending…" : "Request Partner Info"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer style={{ background:C.dark, color:"rgba(255,255,255,.65)", padding:"32px 24px",
        textAlign:"center", fontFamily:FF, fontSize:13 }}>
        <p style={{ margin:"0 0 6px" }}>
          <strong style={{ color:C.white }}>Neighborhood Boxing Club</strong>
        </p>
        <p style={{ margin:"0 0 6px" }}>4324 N High St, Columbus, OH 43214</p>
        <p style={{ margin:0 }}>
          Text Ron:{" "}
          <a href="sms:+16145476096" style={{ color:C.teal }}>
            (614) 547-6096
          </a>
        </p>
      </footer>

    </div>
  );
}
